import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import { useApp } from '../context/AppContext';
import { DOCTORS } from '../data';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const SPECIALTY_FILTERS = [
  { id: 'rei', label: 'REI' },
  { id: 'ob', label: 'OB' },
  { id: 'midwife', label: 'Midwife' },
  { id: 'lactation', label: 'Lactation' },
  { id: 'therapist', label: 'Therapist' },
];

function zipToCity(zip) {
  const z = String(zip || '').replace(/\D/g, '').slice(0, 5);
  const prefix3 = z.slice(0, 3);
  const map = {
    '430': 'Columbus', '431': 'Columbus', '432': 'Columbus', '433': 'Columbus',
    '021': 'Boston', '022': 'Boston', '023': 'Boston', '024': 'Newton',
  };
  return map[prefix3] || (z ? 'your area' : '');
}

function resolveDoctor(entry, doctorsData) {
  if (entry.source === 'data' && entry.doctorId) {
    const d = doctorsData.find((x) => x.id === entry.doctorId);
    if (d) {
      return {
        ...d,
        ...entry,
        displayName: d.name,
        displaySpecialty: d.specialty,
        displayClinic: d.clinic || d.location,
        initials: d.initials,
        avatarBg: d.avatarBg,
        avatarColor: d.avatarColor,
      };
    }
  }
  const n = (entry.name || '?').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  return {
    ...entry,
    displayName: entry.name || 'Unknown',
    displaySpecialty: entry.specialty || '',
    displayClinic: entry.clinic || '',
    initials: n || '?',
    avatarBg: '#FBEAF0',
    avatarColor: '#993556',
  };
}

const btnPrimary = { padding: '10px 20px', background: 'var(--pink)', color: 'white', border: 'none', borderRadius: 999, cursor: 'pointer', fontSize: 14 };
const btnSecondary = { padding: '8px 16px', background: 'var(--sand)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 999, cursor: 'pointer', fontSize: 13 };
const inputStyle = { padding: 10, borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, width: '100%' };

export default function MyDoctors() {
  const navigate = useNavigate();
  const {
    user,
    getMyDoctors,
    addDoctorToCareTeam,
    removeDoctorFromCareTeam,
    updateMyDoctor,
    getDoctorReviews,
    getDoctorStats,
    addDoctorReview,
  } = useApp();

  const [showAddForm, setShowAddForm] = useState(false);
  const finderRef = React.useRef(null);
  const [finderFilter, setFinderFilter] = useState('all');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDoctorId, setReviewDoctorId] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewJourneyContext, setReviewJourneyContext] = useState('');
  const [reviewAnonymous, setReviewAnonymous] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [editingNotesId, setEditingNotesId] = useState(null);

  const [addFormState, setAddFormState] = useState({
    name: '', specialty: '', clinic: '', phone: '', address: '', insurance: '', notes: '', rating: 5,
    insuranceAccepted: true, telehealthAvailable: false, languages: 'English',
  });

  const myDoctors = getMyDoctors(user);
  const zip = user?.zip_code || '';
  const city = zipToCity(zip);

  const filteredFinder = useMemo(() => {
    if (finderFilter === 'all') return DOCTORS;
    const bucketMap = {
      rei: 'ivf',
      ob: 'pregnancy',
      midwife: 'pregnancy',
      lactation: 'postpartum',
      menopause: 'menopause',
      therapist: 'therapist',
    };
    const bucket = bucketMap[finderFilter];
    if (bucket === 'therapist') return [];
    return DOCTORS.filter((d) => d.bucket === bucket || (d.specialty || '').toLowerCase().includes(finderFilter));
  }, [finderFilter]);

  const reviewDoctor = useMemo(() => DOCTORS.find((d) => d.id === reviewDoctorId) || null, [reviewDoctorId]);
  const reviewList = useMemo(() => getDoctorReviews(reviewDoctorId), [getDoctorReviews, reviewDoctorId]);

  function openReview(doctorId) {
    setReviewDoctorId(doctorId);
    setReviewRating(5);
    setReviewText('');
    setReviewJourneyContext('');
    setReviewAnonymous(false);
    setReviewError('');
    setReviewOpen(true);
  }

  function closeReview() {
    setReviewOpen(false);
    setReviewDoctorId(null);
  }

  function handleAddDoctor(e) {
    e?.preventDefault();
    if (!addFormState.name?.trim()) return;
    addDoctorToCareTeam({
      source: 'custom',
      name: addFormState.name.trim(),
      specialty: addFormState.specialty?.trim() || '',
      clinic: addFormState.clinic?.trim() || '',
      phone: addFormState.phone?.trim() || '',
      address: addFormState.address?.trim() || '',
      insurance: addFormState.insurance?.trim() || '',
      notes: addFormState.notes?.trim() || '',
      rating: Number(addFormState.rating) || 5,
      insuranceAccepted: addFormState.insuranceAccepted,
      telehealthAvailable: addFormState.telehealthAvailable,
      languages: addFormState.languages?.trim() || 'English',
    });
    setAddFormState({ name: '', specialty: '', clinic: '', phone: '', address: '', insurance: '', notes: '', rating: 5, insuranceAccepted: true, telehealthAvailable: false, languages: 'English' });
    setShowAddForm(false);
  }

  function handleSaveFromFinder(doctor) {
    addDoctorToCareTeam({
      doctorId: doctor.id,
      notes: '',
      rating: doctor.rating,
      insuranceAccepted: true,
      telehealthAvailable: false,
      languages: 'English',
    });
  }

  const isDoctorSaved = (doctorId) => myDoctors.some((d) => d.doctorId === doctorId);

  return (
    <DashboardAppShell
      fontFamily={FONT_BODY}
      rightColumnGridTrack="220px"
      rightColumn={(
      <aside style={{ padding: 24, borderLeft: '1px solid var(--border)', background: 'white' }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase' }}>Quick actions</h4>
        <button type="button" onClick={() => setShowAddForm(true)} style={{ ...btnPrimary, width: '100%', marginBottom: 8 }}>+ Add doctor</button>
        <button type="button" onClick={() => finderRef.current?.scrollIntoView({ behavior: 'smooth' })} style={{ ...btnSecondary, width: '100%' }}>Find a specialist</button>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16 }}>Nova matches doctors to your chapter, location, and insurance.</p>
      </aside>
      )}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)' }}>My doctors</h1>
          <button type="button" onClick={() => finderRef.current?.scrollIntoView({ behavior: 'smooth' })} style={btnPrimary}>
            Find a specialist
          </button>
        </div>

        {/* MY CARE TEAM */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>My care team</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {myDoctors.map((entry) => {
              const doc = resolveDoctor(entry, DOCTORS);
              const stats = doc.id ? getDoctorStats(DOCTORS.find((d) => d.id === doc.id) || doc) : { rating: entry.rating || doc.rating || 0, reviews: 0 };
              return (
                <div key={entry.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: doc.avatarBg || '#FBEAF0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: doc.avatarColor || '#993556', flexShrink: 0 }}>
                      {doc.initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{doc.displayName}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{doc.displaySpecialty} · {doc.displayClinic}</div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => updateMyDoctor(entry.id, { rating: n })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: (entry.rating || stats.rating) >= n ? '#D4537E' : 'var(--border)' }}>★</button>
                        ))}
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{stats.rating} · {stats.reviews} reviews</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        {(entry.insuranceAccepted !== false) && <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: '#E1F5EE', color: '#0F6E56' }}>Insurance accepted</span>}
                        {entry.telehealthAvailable && <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: '#EEEDFE', color: '#534AB7' }}>Telehealth</span>}
                        {entry.languages && <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: 'var(--sand)', color: 'var(--muted)' }}>{entry.languages}</span>}
                      </div>
                      {editingNotesId === entry.id ? (
                        <div style={{ marginTop: 12 }}>
                          <textarea value={entry.notes || ''} onChange={(e) => updateMyDoctor(entry.id, { notes: e.target.value })} rows={3} style={{ ...inputStyle, marginBottom: 8 }} placeholder="Your private notes..." />
                          <button type="button" onClick={() => setEditingNotesId(null)} style={btnSecondary}>Done</button>
                        </div>
                      ) : (
                        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text)' }}>
                          {entry.notes ? <em>{entry.notes}</em> : <span style={{ color: 'var(--muted)' }}>No notes</span>}
                          <button type="button" onClick={() => setEditingNotesId(entry.id)} style={{ ...btnSecondary, marginLeft: 8, padding: '4px 10px' }}>Edit notes</button>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                        <button type="button" style={btnPrimary}>Book appointment</button>
                        <button type="button" style={btnSecondary}>Message notes</button>
                        <button type="button" onClick={() => removeDoctorFromCareTeam(entry.id)} style={{ ...btnSecondary, color: '#B91C1C' }}>Remove</button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={() => setShowAddForm(true)} style={{ ...btnSecondary, marginTop: 16 }}>+ Add doctor</button>
        </section>

        {/* Add doctor form modal */}
        {showAddForm && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 9999 }} onMouseDown={(e) => e.target === e.currentTarget && setShowAddForm(false)}>
            <div style={{ width: '100%', maxWidth: 480, background: 'white', borderRadius: 16, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: FONT_SERIF, fontSize: 20, marginBottom: 20 }}>Add doctor</h3>
              <form onSubmit={handleAddDoctor} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {['name', 'specialty', 'clinic', 'phone', 'address', 'insurance'].map((f) => (
                  <div key={f}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'capitalize', display: 'block', marginBottom: 4 }}>{f}</label>
                    <input value={addFormState[f] || ''} onChange={(e) => setAddFormState((s) => ({ ...s, [f]: e.target.value }))} style={inputStyle} placeholder={f} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Notes</label>
                  <textarea value={addFormState.notes || ''} onChange={(e) => setAddFormState((s) => ({ ...s, notes: e.target.value }))} rows={3} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Rating (1-5)</label>
                  <select value={addFormState.rating} onChange={(e) => setAddFormState((s) => ({ ...s, rating: Number(e.target.value) }))} style={inputStyle}>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>★ {n}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <button type="submit" style={btnPrimary}>Add doctor</button>
                  <button type="button" onClick={() => setShowAddForm(false)} style={btnSecondary}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* DOCTOR FINDER */}
        <section ref={finderRef} style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>Find a specialist near you</h2>
            <div style={{ background: '#FBEAF0', border: '1px solid #F4C0D1', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--text)' }}><strong>Nova-powered search</strong> — using ZIP {zip || '(add in profile)'} · {city ? `${city}` : 'your area'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <button type="button" onClick={() => setFinderFilter('all')} style={{ ...btnSecondary, background: finderFilter === 'all' ? 'var(--pink)' : undefined, color: finderFilter === 'all' ? 'white' : undefined }}>All</button>
              {SPECIALTY_FILTERS.map((f) => (
                <button key={f.id} type="button" onClick={() => setFinderFilter(f.id)} style={{ ...btnSecondary, background: finderFilter === f.id ? 'var(--pink)' : undefined, color: finderFilter === f.id ? 'white' : undefined }}>{f.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredFinder.length === 0 ? (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
                  Nova is finding specialists near {city || 'your area'}...
                </div>
              ) : (
                filteredFinder.map((d) => {
                  const stats = getDoctorStats(d);
                  const saved = isDoctorSaved(d.id);
                  return (
                    <div key={d.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 18, display: 'flex', gap: 16 }}>
                      <div style={{ width: 48, height: 48, borderRadius: '50%', background: d.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: d.avatarColor, flexShrink: 0 }}>{d.initials}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{d.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{d.specialty} · {d.distance || d.location}</div>
                        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>★ {stats.rating} · {d.clinic} · Insurance accepted</div>
                        <button type="button" onClick={() => saved || handleSaveFromFinder(d)} disabled={saved} style={{ ...btnPrimary, marginTop: 10, opacity: saved ? 0.6 : 1 }}>
                          {saved ? 'Saved' : 'Save to my doctors'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

        {/* REVIEWS */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>Reviews from the community</h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>Women with similar journeys have reviewed these doctors. Add a review for your own doctors.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {DOCTORS.slice(0, 4).map((d) => {
              const stats = getDoctorStats(d);
              const reviews = getDoctorReviews(d.id);
              return (
                <div key={d.id} style={{ flex: '1 1 200px', maxWidth: 280, background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{d.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>★ {stats.rating} · {stats.reviews} reviews</div>
                  {reviews.slice(0, 2).map((r) => (
                    <div key={r.id} style={{ marginTop: 10, padding: 10, background: 'var(--sand)', borderRadius: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.userName} · ★ {r.rating}</div>
                      <div style={{ color: 'var(--text)' }}>{r.text}</div>
                    </div>
                  ))}
                  <button type="button" onClick={() => openReview(d.id)} style={{ ...btnSecondary, marginTop: 12, width: '100%' }}>Add review</button>
                </div>
              );
            })}
          </div>
        </section>

      {/* Review modal */}
      {reviewOpen && reviewDoctor && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, zIndex: 9999 }} onMouseDown={(e) => e.target === e.currentTarget && closeReview()}>
          <div style={{ width: '100%', maxWidth: 520, background: 'white', borderRadius: 16, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <h3 style={{ fontFamily: FONT_SERIF, fontSize: 18 }}>Review {reviewDoctor.name}</h3>
              <button type="button" onClick={closeReview} style={btnSecondary}>Close</button>
            </div>
            {!user && <p style={{ background: '#FBEAF0', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>Please sign in to submit a review.</p>}
            {reviewList.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>RECENT REVIEWS</div>
                {reviewList.slice(0, 2).map((r) => (
                  <div key={r.id} style={{ padding: 12, background: 'var(--sand)', borderRadius: 8, marginBottom: 8, fontSize: 12 }}><strong>{r.userName}</strong> ★ {r.rating}<br />{r.text}</div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Your rating</label>
              <div style={{ display: 'flex', gap: 8 }}>{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setReviewRating(n)} style={{ ...btnSecondary, background: reviewRating === n ? '#FBEAF0' : undefined }}>★ {n}</button>)}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Your review</label>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={4} placeholder="Share what went well and how the doctor made you feel." style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Journey context (optional)</label>
              <input value={reviewJourneyContext} onChange={(e) => setReviewJourneyContext(e.target.value)} placeholder="e.g. IVF cycle 2, postpartum 6 weeks" style={inputStyle} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13 }}><input type="checkbox" checked={reviewAnonymous} onChange={(e) => setReviewAnonymous(e.target.checked)} />Post anonymously</label>
            {reviewError && <div style={{ background: 'rgba(185,70,70,0.1)', color: '#B91C1C', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{reviewError}</div>}
            {user && (
              <button
                type="button"
                onClick={() => {
                  const res = addDoctorReview({ doctorId: reviewDoctorId, rating: reviewRating, text: reviewText, journeyContext: reviewJourneyContext || undefined, isAnonymous: reviewAnonymous });
                  if (!res?.ok) { setReviewError(res?.error || 'Could not save.'); return; }
                  closeReview();
                }}
                style={btnPrimary}
              >
                Submit review
              </button>
            )}
          </div>
        </div>
      )}
    </DashboardAppShell>
  );
}
